import { bancoDelIban } from '@erp/shared';

/**
 * Qué banco y qué SWIFT poner al cambiar el IBAN en el formulario de una cuenta.
 *
 * La clave está en comparar con la **entidad que originó los valores actuales**, no con el IBAN de la
 * pulsación anterior: al teclear carácter a carácter, el texto intermedio ya contiene la entidad nueva y
 * compararlo con él dejaría la pantalla sin rellenar nunca.
 *
 * Reglas:
 * - IBAN a medio escribir → no se toca nada.
 * - Mismo banco que el de los valores actuales → no se toca nada (corregir un dígito de la cuenta no
 *   puede renombrar el banco ni cambiar el BIC que el usuario puso a mano).
 * - Otro banco → se sustituye **solo** lo que puso el autocompletado; lo escrito por el usuario se queda.
 * - IBAN completo sin banco resuelto (extranjero o entidad desconocida) → se vacía lo autocompletado,
 *   porque quedarse con el BIC del banco anterior es pagar a la entidad equivocada. Si los valores no
 *   venían de un banco reconocido, no se toca nada: es una cuenta que se escribe a mano.
 */
export interface CuentaEnFormulario {
  iban: string;
  bankName: string;
  swift: string;
}

/** Lo último que puso la pantalla, y de qué banco salió (`entidad` vacía si no salió de ninguno). */
export interface RellenadoAutomatico {
  bankName: string;
  swift: string;
  entidad: string;
}

/** IBAN completo: español de 24 caracteres, o extranjero con la longitud de un IBAN. */
export function ibanCompleto(iban: string): boolean {
  const limpio = iban.replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(limpio)) return false;
  if (limpio.startsWith('ES')) return limpio.length === 24;
  return limpio.length >= 15 && limpio.length <= 34;
}

/** Entidad de un IBAN español (`ESkk EEEE …`), o `''` si no es español. */
export function entidadDelIban(iban: string): string {
  const limpio = iban.replace(/\s+/g, '').toUpperCase();
  return limpio.startsWith('ES') && limpio.length >= 8 ? limpio.slice(4, 8) : '';
}

/**
 * Lo que hay que dar por «ya rellenado» al abrir una cuenta guardada: sus valores y la entidad del banco
 * que los originó. Si el IBAN es de una entidad que no está en la tabla, la entidad es `''`, para que
 * corregir un dígito no borre lo que el usuario escribió a mano.
 */
export function rellenadoDeGuardado(fila: { iban: string; bankName: string | null; swift: string | null }): RellenadoAutomatico {
  return {
    bankName: fila.bankName ?? '',
    swift: fila.swift ?? '',
    entidad: bancoDelIban(fila.iban)?.entidad ?? ''
  };
}

export function aplicarIban(
  anterior: CuentaEnFormulario,
  rellenado: RellenadoAutomatico,
  ibanNuevo: string
): { cuenta: CuentaEnFormulario; rellenado: RellenadoAutomatico } {
  const sinCambios = { cuenta: { ...anterior, iban: ibanNuevo }, rellenado };
  if (!ibanCompleto(ibanNuevo)) return sinCambios;

  const banco = bancoDelIban(ibanNuevo);
  const puedeBanco = anterior.bankName === '' || anterior.bankName === rellenado.bankName;
  const puedeSwift = anterior.swift === '' || anterior.swift === rellenado.swift;

  if (banco) {
    if (banco.entidad === rellenado.entidad) return sinCambios;
    return {
      cuenta: {
        ...anterior,
        iban: ibanNuevo,
        bankName: puedeBanco ? banco.nombre : anterior.bankName,
        swift: puedeSwift ? banco.bic : anterior.swift
      },
      rellenado: {
        bankName: puedeBanco ? banco.nombre : rellenado.bankName,
        swift: puedeSwift ? banco.bic : rellenado.swift,
        entidad: banco.entidad
      }
    };
  }

  // Sin banco resuelto: solo se vacía si lo que hay venía de un banco reconocido.
  if (rellenado.entidad === '') return sinCambios;
  return {
    cuenta: {
      ...anterior,
      iban: ibanNuevo,
      bankName: puedeBanco ? '' : anterior.bankName,
      swift: puedeSwift ? '' : anterior.swift
    },
    rellenado: {
      bankName: puedeBanco ? '' : rellenado.bankName,
      swift: puedeSwift ? '' : rellenado.swift,
      entidad: ''
    }
  };
}
